import { registerOp } from '../endpoints'
import { getTransport } from '../transport'
import { commitSuccess } from '../success-write'
import API from 'src/api'
import { IdMappingRepository } from '../../repositories/IdMappingRepository'
import { MetaRepository } from '../../repositories/MetaRepository'
import { isServerId } from '../../repositories/ids'
import { StorageAdapter } from '../../storage/StorageAdapter'
import { firstRow } from '../../storage/rows'
import {
  buildCreatePayload,
  buildCreateStandalonePayload,
  buildUpdateFieldsPayload,
  buildChangeStatusPayload,
  buildEvidencePayload,
  buildTaskPicturePayload
} from '../builders/tasks-builder'
import { taskEvidenceRepository } from '../../repositories/TaskEvidenceRepository'
import { dbgWarn } from '../../logger'

/**
 * Un create con 2xx pero SIN server_id resoluble (shape de respuesta inesperado) NO debe commitear
 * un `done` sin id_mapping: todas las hijas (updateFields/changeStatus/evidence) caerían a dead-letter
 * después con un síntoma críptico. Se lanza un error DESCRIPTIVO aquí mismo -> la fila del create surge
 * el problema y reintenta (fromForms re-403ea por dedup de título y recupera el id). [review Hugo]
 */
function assertResolvableServerId (serverId, opLabel, clientUuid) {
  if (serverId == null) {
    dbgWarn('sync', `${opLabel}: 2xx SIN server_id (shape inesperado) client_uuid=${clientUuid} -> reintenta (no commitea done sin mapping)`)
    throw new Error(`${opLabel}: 2xx sin server_id resoluble para client_uuid=${clientUuid}`)
  }
}
import { makeResolveBase64 } from '../../photos/resolveBlob'
import { idemHeader, is2xx, throwFromResponse } from '../http-helpers'

/**
 * modules/tasks.js — handlers de los ops de task que F2 emite (create / createStandalone /
 * updateFields / changeStatus / evidence / delete / deleteStandalone). Re-implementacion LIMPIA
 * de src/offline/modules/tasks.js + task-actions.js (REFERENCIA — NO importado).
 *
 * HIDRATAR-AL-DRENAR (PUSH-04/05): los ops sobre una task EXISTENTE (updateFields/changeStatus/
 * evidence/delete) RESUELVEN client_uuid->server_id via IdMappingRepository.serverIdFor al drenar.
 * Si el server_id aun NO existe (el create padre no dreno todavia) el handler LANZA un Error PLANO
 * (sin status) -> classify lo trata como retry transitorio (NUNCA dead); FIFO por seq garantiza
 * que el create padre drena antes. Child-before-parent resuelto.
 *
 * CONTRATO commitSuccess LOCKED: tras exito, commitSuccess(...) (success-write atomico) + return
 * { committed: true }. El onSuccess de 04-01 corta-circuito con `if (result.committed) return`.
 * Los pre-checks idempotentes (marca meta presente) corto-circuitan SIN red y tambien commitean.
 *
 * 403/404 op-aware: los handlers DEJAN PROPAGAR el rechazo (no lo tragan); el clasificador del
 * drain mapea 403+task.create -> success, 404+delete -> success, 403+delete -> dead.
 *
 * Header Idempotency-Key = row.mutation_uuid en cada request.
 */

const GRAPHQL_URL = API.GRAPHQL_URL

// resolver de base64 REAL de fotos (F5): lee el binario de evidencia del backend activo (web idb /
// FS) como string base64 — picture/file espera un string, no un Blob (evita FileReader, funciona en
// node y device).
const resolveBase64 = makeResolveBase64()

/**
 * Resuelve el base64 del binario de evidencia picture/file, leyendo la fila task_evidence por su
 * uuid para obtener image_ref/local_path y luego el binario del backend. Devuelve null si no hay
 * binario (comment, o foto aun no recuperable) -> el builder es tolerante (base64 null).
 * @param {object} body  body del op (incluye uuid de la evidencia).
 * @returns {Promise<string|null>} base64 (dataURL en web / crudo en nativo) o null.
 */
async function resolveEvidenceBase64 (body) {
  const evRow = await taskEvidenceRepository.get(body.uuid)
  const record = {
    image_ref: (evRow && evRow.image_ref) || body.image_ref,
    local_path: evRow && evRow.local_path
  }
  if (!record.image_ref) return null
  return resolveBase64(record)
}

/**
 * Extrae el server_id de la respuesta de create. fromForms responde { success: task };
 * store (standalone) responde la Task resource directo. Se lee defensivo:
 * res.data.success.id | res.data.data.id | res.data.id.
 */
function extractServerId (res) {
  const d = (res && res.data) || {}
  if (d.success && typeof d.success === 'object' && d.success.id != null) return d.success.id
  if (d.data && typeof d.data === 'object' && d.data.id != null) return d.data.id
  if (d.id != null) return d.id
  return null
}

/**
 * Resuelve el server_id de una task.
 *
 * PR review F4/F6:
 *  - `srv:task:<id>` (uuid SINTETICO de OccurrenceTaskCard.taskClientUuidV2 para tasks del server —
 *    p.ej. tasks de OTROS usuarios vistas en la occurrence, que ningún pull mapea a id_mapping)
 *    trae el server_id EMBEBIDO: se parsea directo, sin id_mapping.
 *  - Sin mapping: si el create PADRE (mismo client_uuid) sigue vivo (pending/in_flight) se ESPERA
 *    sin consumir retries (waitingDependency -> throttle; antes: 10 retries -> dead-letter aunque
 *    solo estaba esperando). Si el padre esta 'dead' o ya no existe y el mapping es imposible, la
 *    espera es inutil -> dependencyDead (fail-fast a dead, visible YA en el drawer).
 */
async function resolveTaskServerId (clientUuid, opLabel) {
  const synthetic = /^srv:task:(\d+)$/.exec(String(clientUuid || ''))
  if (synthetic) return Number(synthetic[1])

  const serverId = await IdMappingRepository.serverIdFor('task', clientUuid)
  if (serverId == null) {
    const parent = firstRow(await StorageAdapter.query(
      "SELECT status FROM outbox WHERE client_uuid=? AND op IN ('task.create','task.createStandalone') ORDER BY seq DESC",
      [clientUuid]
    ))
    const parentAlive = parent && (parent.status === 'pending' || parent.status === 'in_flight')
    if (parentAlive) {
      const waitErr = new Error(`${opLabel}: server_id pendiente para client_uuid=${clientUuid} (create padre en cola)`)
      waitErr.waitingDependency = true
      throw waitErr
    }
    const parentState = parent ? parent.status : 'inexistente'
    const deadErr = new Error(`${opLabel}: server_id irrecuperable para client_uuid=${clientUuid} (create padre ${parentState} sin mapping)`)
    deadErr.dependencyDead = true
    throw deadErr
  }
  return serverId
}

/**
 * F5 (PR review): el 403 de fromForms ("La tarea ya existe", dedup por occurrence+titulo) NO trae el
 * id de la task existente -> antes la fila quedaba done SIN id_mapping y TODAS las ops hijas
 * (updateFields/changeStatus/evidence) quedaban irresolubles. Recupera el id real con el endpoint de
 * reload (GET tasks/<occId>/occurrence, mismo que usa reloadTasks online) casando por titulo.
 * Un GET no-2xx LANZA (transitorio -> reintenta el create completo, que es idempotente: re-403 y
 * re-intenta la recuperacion). 2xx sin match -> null (el caller cae al 403->success de siempre).
 */
async function recoverExistingTaskId (task = {}) {
  const occId = task.ocurrence_id ?? task.occurrence_id
  const title = task.title
  if (occId == null || title == null || title === '') return null
  const res = await getTransport().get(`${GRAPHQL_URL}tasks/${occId}/occurrence`)
  if (!is2xx(res)) throwFromResponse(res)
  const d = (res && res.data) || {}
  const candidates = [
    ...(Array.isArray(d.task_on_form) ? d.task_on_form : []),
    ...(Array.isArray(d.task_on_occ_editing) ? d.task_on_occ_editing : [])
  ]
  // `task_on_form` trae tareas del mismo form+location de CUALQUIER ocurrencia; el dedup del backend
  // (fromForms) es por ocurrencia+título, así que solo cuenta una tarea de ESTA ocurrencia. Sin este
  // filtro, un título repetido de otra auditoría ligaba las fotos/campos a la tarea equivocada.
  const match = candidates.find((t) => t && String(t.title) === String(title) && String(t.ocurrence_id) === String(occId))
  return match && isServerId(match.id) ? Number(match.id) : null
}

export function registerTaskOps () {
  // ── task.create (in-occurrence) ───────────────────────────────────────────────
  // POST tasks/fromForms. 200 {success:task} -> id_mapping. 403 = ya existe (dedup titulo) ->
  // F5: se intenta RECUPERAR el id de la task existente y commitear el mapping (las hijas lo
  // necesitan); si no hay match se propaga y classify mapea 403+task.create -> success (como antes).
  registerOp('task.create', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const res = await getTransport().post(
      `${GRAPHQL_URL}tasks/fromForms`,
      buildCreatePayload(body.task),
      idemHeader(row)
    )
    if (!is2xx(res)) {
      if (res && res.status === 403) {
        const recoveredId = await recoverExistingTaskId(body.task)
        if (recoveredId != null) {
          await commitSuccess({ seq: row.seq, entity: 'task', clientUuid: row.client_uuid, serverId: recoveredId, metaKeys: [] })
          return { committed: true, server_id: recoveredId }
        }
      }
      throwFromResponse(res)
    }

    const serverId = extractServerId(res)
    assertResolvableServerId(serverId, 'task.create', row.client_uuid)
    await commitSuccess({ seq: row.seq, entity: 'task', clientUuid: row.client_uuid, serverId, metaKeys: [] })
    return { committed: true, server_id: serverId }
  })

  // ── task.createStandalone ─────────────────────────────────────────────────────
  // POST tasks (store, create INGENUO sin dedup). PRE-CHECK tcreated:<clientUuid> -> idempotente
  // sin red (mitiga el riesgo de duplicado por respuesta perdida). 200 -> id_mapping + marca.
  registerOp('task.createStandalone', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const clientUuid = row.client_uuid
    const createdKey = MetaRepository.taskCreatedKey(clientUuid)

    if (await MetaRepository.has(createdKey)) {
      // ya creada (respuesta perdida): finalizar sin reenviar (NO duplicar el create ingenuo).
      const serverId = await IdMappingRepository.serverIdFor('task', clientUuid)
      await commitSuccess({ seq: row.seq, entity: 'task', clientUuid, serverId, metaKeys: [createdKey] })
      return { committed: true, ok: true }
    }

    const res = await getTransport().post(
      `${GRAPHQL_URL}tasks`,
      buildCreateStandalonePayload(body.task),
      idemHeader(row)
    )
    if (!is2xx(res)) throwFromResponse(res)

    const serverId = extractServerId(res)
    // NOTA: aquí NO se lanza en serverId null (a diferencia de task.create/fromForms): POST tasks es un
    // create INGENUO sin dedup server-side, así que reintentar DUPLICARÍA. Se marca tcreated + se commitea
    // (la task existe: el 2xx lo confirma); si el id no vino, las hijas lo re-resuelven cuando aparezca el
    // mapping y, si no, dead-lettean con su propio mensaje. La visibilidad del create sin-id va en fromForms.
    if (serverId == null) dbgWarn('sync', `task.createStandalone: 2xx SIN server_id client_uuid=${clientUuid} (hijas podrían quedar sin resolver)`)
    await commitSuccess({ seq: row.seq, entity: 'task', clientUuid, serverId, metaKeys: [createdKey] })
    return { committed: true, server_id: serverId }
  })

  // ── task.updateFields ─────────────────────────────────────────────────────────
  // RESUELVE server_id (transient throw si null). POST tasks/updateFilds. Cada edicion lleva su
  // edit_uuid unico (no coalesce) -> marca tedit:<client_uuid>.
  registerOp('task.updateFields', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const clientUuid = body.client_uuid
    const serverId = await resolveTaskServerId(clientUuid, 'task.updateFields')

    const res = await getTransport().post(
      `${GRAPHQL_URL}tasks/updateFilds`,
      buildUpdateFieldsPayload(serverId, body.changes),
      idemHeader(row)
    )
    if (!is2xx(res)) throwFromResponse(res)

    await commitSuccess({ seq: row.seq, metaKeys: [MetaRepository.taskEditKey(clientUuid)] })
    return { committed: true, ok: true }
  })

  // ── task.changeStatus ─────────────────────────────────────────────────────────
  // PRE-CHECK tstatus:<client_uuid>:<state> -> idempotente sin red. RESUELVE server_id (transient
  // throw si null). POST tasks/changeStatus -> marca tstatus.
  registerOp('task.changeStatus', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const clientUuid = body.client_uuid
    const state = body.state
    const statusKey = MetaRepository.taskStatusKey(clientUuid, state)

    if (await MetaRepository.has(statusKey)) {
      await commitSuccess({ seq: row.seq, metaKeys: [statusKey] })
      return { committed: true, ok: true }
    }

    const serverId = await resolveTaskServerId(clientUuid, 'task.changeStatus')
    // los campos del payload son el body menos las llaves de control (status_key/client_uuid).
    const fields = { ...body }
    delete fields.status_key
    delete fields.client_uuid
    const res = await getTransport().post(
      `${GRAPHQL_URL}tasks/changeStatus`,
      buildChangeStatusPayload(serverId, fields),
      idemHeader(row)
    )
    if (!is2xx(res)) throwFromResponse(res)

    await commitSuccess({ seq: row.seq, metaKeys: [statusKey] })
    return { committed: true, ok: true }
  })

  // ── task.evidence ─────────────────────────────────────────────────────────────
  // PRE-CHECK evsent:<uuid> -> idempotente sin red. RESUELVE server_id de la task (transient throw
  // si null). POST tasks/answer/{serverId}.
  //
  // LINEA F3/F5 RESUELTA (F5): F3 manejaba comment-evidence completa y enviaba picture/file con
  // base64=null (solo-ref). F5 adjunta el BINARIO real: para picture/file se lee la fila
  // task_evidence por uuid -> image_ref/local_path -> backend.read -> Blob -> base64 (dataURL),
  // que se pasa al builder tolerante. Comment sigue sin binario (comment-only). Si el binario aun
  // no es recuperable, resolveEvidenceBase64 devuelve null y el builder degrada a solo-ref (la
  // cola NUNCA se bloquea, la fila NUNCA se pierde).
  registerOp('task.evidence', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const evUuid = body.uuid
    const evKey = MetaRepository.evSentKey(evUuid)

    if (await MetaRepository.has(evKey)) {
      await commitSuccess({ seq: row.seq, metaKeys: [evKey] })
      return { committed: true, ok: true }
    }

    const serverId = await resolveTaskServerId(body.task_client_uuid, 'task.evidence')
    // picture/task_picture/file: resuelve el binario real; comment: sin binario.
    const base64 = (body.kind === 'picture' || body.kind === 'task_picture' || body.kind === 'file')
      ? await resolveEvidenceBase64(body)
      : null

    // task_picture = foto de la TAREA (no evidencia de cumplimiento): mismo endpoint que el camino
    // online (TasksService.updateFildImages), que la guarda con type 'task_picture' y la expone en
    // `task_pictures`. Por tasks/answer quedaba como type 'picture' e invisible al recargar.
    if (body.kind === 'task_picture') {
      // Sin binario, updateFildImages responde 200 ("no actions") SIN crear fila: commitear ahí
      // perdería la foto en silencio. Error plano sin status -> classify: retry con backoff y
      // dead-letter visible tras MAX_RETRIES (no bloquea la cola: las demás filas siguen drenando).
      if (base64 == null) {
        throw new Error(`task.evidence(task_picture): binario no recuperable para evidencia ${evUuid}`)
      }
      const res = await getTransport().post(
        `${GRAPHQL_URL}tasks/updateFildImages`,
        buildTaskPicturePayload({ taskId: serverId, base64 }),
        idemHeader(row)
      )
      if (!is2xx(res)) throwFromResponse(res)

      // id del answer creado -> id_mapping('task_evidence', evUuid): la foto optimista (id null en
      // memoria) lo resuelve al editar/borrar sin recargar la ocurrencia (resolveSyncedTaskPhoto).
      const answerId = res && res.data && isServerId(res.data.id) ? Number(res.data.id) : null
      await commitSuccess({ seq: row.seq, entity: 'task_evidence', clientUuid: evUuid, serverId: answerId, metaKeys: [evKey] })
      return { committed: true, ok: true }
    }

    const payload = buildEvidencePayload({
      kind: body.kind,
      base64,
      comment: body.comment,
      filename: body.filename
    })
    const res = await getTransport().post(
      `${GRAPHQL_URL}tasks/answer/${serverId}`,
      payload,
      idemHeader(row)
    )
    if (!is2xx(res)) throwFromResponse(res)

    await commitSuccess({ seq: row.seq, metaKeys: [evKey] })
    return { committed: true, ok: true }
  })

  // ── task.delete ─────────────────────────────────────────────────────────────
  // RESUELVE server_id (transient throw si null). DELETE tasks/{serverId}. 404 -> success
  // (classify), 403 -> dead (classify). Se deja PROPAGAR el rechazo.
  registerOp('task.delete', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const serverId = await resolveTaskServerId(body.client_uuid, 'task.delete')
    const res = await getTransport().delete(`${GRAPHQL_URL}tasks/${serverId}`, idemHeader(row))
    if (!is2xx(res)) throwFromResponse(res)

    await commitSuccess({ seq: row.seq, metaKeys: [] })
    return { committed: true, ok: true }
  })

  // ── task.deleteStandalone ────────────────────────────────────────────────────
  // Igual semantica que task.delete. Acepta { client_uuid } o { server_id } directo.
  registerOp('task.deleteStandalone', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const serverId = body.server_id != null
      ? body.server_id
      : await resolveTaskServerId(body.client_uuid, 'task.deleteStandalone')
    const res = await getTransport().delete(`${GRAPHQL_URL}tasks/${serverId}`, idemHeader(row))
    if (!is2xx(res)) throwFromResponse(res)

    await commitSuccess({ seq: row.seq, metaKeys: [] })
    return { committed: true, ok: true }
  })
}
